"use client";

import { c as _c } from "react/compiler-runtime";
import { createContext, use, useCallback, useState } from "react";
import { BiSolidLockAlt } from "react-icons/bi";
import { FaArrowLeft } from "react-icons/fa";
import { FiPlus } from "react-icons/fi";
import PayinSessionSurface from "@/components/payments/payin-session-surface";
import { useRainforestPayinSessionContext } from "@/components/rainforest/rainforest-payin-session-context";
import { payinResponseToSession } from "@/components/rainforest/use-rainforest-payin-session";
import { Button } from "@/components/ui";
import { debitOrCreditFromBinLookup } from "@/lib/billing/debit-or-credit-from-bin-lookup";
import { PaymentMethodPatient } from "@/types/payment";
import { BinLookupResult } from "@/types/rainforest";
import { PaymentMethodCard } from "./billing/payment-method-card";
interface PaymentMethodSelectorContextValue {
  state: {
    userChoseAddNew: boolean;
    selectedSavedMethodId: string | null;
  };
  actions: {
    chooseAddNew: () => void;
    chooseSelectSaved: () => void;
    setSelectedSavedMethodId: (id: string | null) => void;
  };
}
const PaymentMethodSelectorContext = createContext<PaymentMethodSelectorContextValue | null>(null);
function usePaymentMethodSelector(): PaymentMethodSelectorContextValue {
  const ctx = use(PaymentMethodSelectorContext);
  if (!ctx) {
    throw new Error("PaymentMethodSelector subcomponents must be used inside <PaymentMethodSelector.Provider>");
  }
  return ctx;
}
function Provider(t0) {
  const $ = _c(9);
  const {
    children,
    defaultSelectedSavedMethodId: t1
  } = t0;
  const defaultSelectedSavedMethodId = t1 === undefined ? null : t1;
  const [userChoseAddNew, setUserChoseAddNew] = useState(false);
  const [selectedSavedMethodId, setSelectedSavedMethodId] = useState(defaultSelectedSavedMethodId);
  let t2;
  if ($[0] !== selectedSavedMethodId || $[1] !== userChoseAddNew) {
    t2 = {
      userChoseAddNew,
      selectedSavedMethodId
    };
    $[0] = selectedSavedMethodId;
    $[1] = userChoseAddNew;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      chooseAddNew: () => setUserChoseAddNew(true),
      chooseSelectSaved: () => setUserChoseAddNew(false),
      setSelectedSavedMethodId
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t2) {
    t4 = {
      state: t2,
      actions: t3
    };
    $[4] = t2;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== children || $[7] !== t4) {
    t5 = <PaymentMethodSelectorContext value={t4}>{children}</PaymentMethodSelectorContext>;
    $[6] = children;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  return t5;
}
function WhenSelectingSaved(t0) {
  const $ = _c(2);
  const {
    children
  } = t0;
  const {
    state
  } = usePaymentMethodSelector();
  if (state.userChoseAddNew) {
    return null;
  }
  let t1;
  if ($[0] !== children) {
    t1 = <>{children}</>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
function WhenAddingNew(t0) {
  const $ = _c(2);
  const {
    children
  } = t0;
  const {
    state
  } = usePaymentMethodSelector();
  if (!state.userChoseAddNew) {
    return null;
  }
  let t1;
  if ($[0] !== children) {
    t1 = <>{children}</>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
function SavedCards(t0) {
  const $ = _c(9);
  const {
    methods
  } = t0;
  const {
    state: t1,
    actions: t2
  } = usePaymentMethodSelector();
  const {
    selectedSavedMethodId
  } = t1;
  const {
    setSelectedSavedMethodId
  } = t2;
  let t3;
  if ($[0] !== methods || $[1] !== selectedSavedMethodId || $[2] !== setSelectedSavedMethodId) {
    let t4;
    if ($[4] !== selectedSavedMethodId || $[5] !== setSelectedSavedMethodId) {
      t4 = method => <PaymentMethodCard key={method.id} className="w-full min-w-0" method={method} isSelected={selectedSavedMethodId === method.id} onSelect={() => setSelectedSavedMethodId(method.id)} isSelectable={true} />;
      $[4] = selectedSavedMethodId;
      $[5] = setSelectedSavedMethodId;
      $[6] = t4;
    } else {
      t4 = $[6];
    }
    t3 = methods.map(t4);
    $[0] = methods;
    $[1] = selectedSavedMethodId;
    $[2] = setSelectedSavedMethodId;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[7] !== t3) {
    t4 = <ul className="grid grid-cols-2 gap-1 md:grid-cols-3">{t3}</ul>;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
}
function PayWithSavedButton(t0) {
  const $ = _c(10);
  const {
    isLoading: t1,
    disabled: t2,
    onPay,
    children
  } = t0;
  const isLoading = t1 === undefined ? false : t1;
  const disabled = t2 === undefined ? false : t2;
  const {
    state: t3
  } = usePaymentMethodSelector();
  const {
    selectedSavedMethodId
  } = t3;
  let t4;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <BiSolidLockAlt />;
    $[0] = t4;
  } else {
    t4 = $[0];
  }
  const t5 = !selectedSavedMethodId || isLoading || disabled;
  let t6;
  if ($[1] !== onPay || $[2] !== selectedSavedMethodId) {
    t6 = async () => {
      if (!selectedSavedMethodId) {
        return;
      }
      await onPay(selectedSavedMethodId);
    };
    $[1] = onPay;
    $[2] = selectedSavedMethodId;
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  const t7 = isLoading ? "Processing\u2026" : children;
  let t8;
  if ($[4] !== t7) {
    t8 = <span>{t7}</span>;
    $[4] = t7;
    $[5] = t8;
  } else {
    t8 = $[5];
  }
  let t9;
  if ($[6] !== t5 || $[7] !== t6 || $[8] !== t8) {
    t9 = <Button variant="primary" className="w-full !py-3 text-body-lg" icon={t4} disabled={t5} onClick={t6}>{t8}</Button>;
    $[6] = t5;
    $[7] = t6;
    $[8] = t8;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  return t9;
}
function AddNewCardButton(t0) {
  const $ = _c(4);
  const {
    onClick
  } = t0;
  const {
    actions: t1
  } = usePaymentMethodSelector();
  const {
    chooseAddNew
  } = t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <FiPlus />;
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] !== chooseAddNew || $[2] !== onClick) {
    t3 = <Button variant="secondary" className="w-full border-dashed" icon={t2} onClick={() => {
      chooseAddNew();
      onClick?.();
    }}>Add a new card</Button>;
    $[1] = chooseAddNew;
    $[2] = onClick;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
}
function BackToSavedCardsButton() {
  const $ = _c(3);
  const {
    actions: t0
  } = usePaymentMethodSelector();
  const {
    chooseSelectSaved
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <FaArrowLeft className="text-xxs" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== chooseSelectSaved) {
    t2 = <Button variant="ghost" onClick={chooseSelectSaved} icon={t1}>Back to saved cards</Button>;
    $[1] = chooseSelectSaved;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}
function NewCardSession(t0) {
  const $ = _c(15);
  const {
    allowApplePay: t1,
    allowAch: t2,
    onSuccess,
    onFailure,
    className
  } = t0;
  const allowApplePay = t1 === undefined ? true : t1;
  const allowAch = t2 === undefined ? true : t2;
  const {
    payinResponse,
    setDebitOrCredit
  } = useRainforestPayinSessionContext();
  let t3;
  if ($[0] !== payinResponse) {
    t3 = payinResponseToSession(payinResponse);
    $[0] = payinResponse;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const session = t3;
  let t4;
  if ($[2] !== setDebitOrCredit) {
    t4 = result => {
      const debitOrCredit = debitOrCreditFromBinLookup(result);
      if (debitOrCredit !== null) {
        setDebitOrCredit(debitOrCredit);
      }
    };
    $[2] = setDebitOrCredit;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const handleBinLookup = t4;
  let methods;
  if ($[4] !== allowAch || $[5] !== allowApplePay) {
    methods = ["CARD"];
    if (allowAch) {
      methods.push("ACH");
    }
    if (allowApplePay) {
      methods.push("APPLE_PAY");
    }
    $[4] = allowAch;
    $[5] = allowApplePay;
    $[6] = methods;
  } else {
    methods = $[6];
  }
  const allowedMethods = methods.join(",");
  let t5;
  if ($[7] !== allowApplePay || $[8] !== allowedMethods || $[9] !== className || $[10] !== handleBinLookup || $[11] !== onFailure || $[12] !== onSuccess || $[13] !== session) {
    t5 = <PayinSessionSurface session={session} onSuccess={onSuccess} onFailure={onFailure} onBinLookup={handleBinLookup} allowedMethods={allowedMethods} allowApplePay={allowApplePay} className={className} />;
    $[7] = allowApplePay;
    $[8] = allowedMethods;
    $[9] = className;
    $[10] = handleBinLookup;
    $[11] = onFailure;
    $[12] = onSuccess;
    $[13] = session;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  return t5;
}
export const PaymentMethodSelector = {
  Provider,
  WhenSelectingSaved,
  WhenAddingNew,
  SavedCards,
  PayWithSavedButton,
  AddNewCardButton,
  BackToSavedCardsButton,
  NewCardSession
};